import { useTranslations } from "next-intl";
import { useIsSSR } from "react-aria";

import { CheckmarkFilledIcon } from "~/components/icons/checkmark-filled";
import { LoadingSpinner } from "~/components/loading-spinner";
import { Button } from "~/components/ui/button";
import { toast } from "~/components/ui/use-toast";
import { type ProductId } from "~/lib/tours";
import { useCart } from "~/lib/use-cart";
import { type TourCalendar } from "~/lib/use-tour-calendar";
import { useTrack } from "~/lib/use-track";
import { usePathname } from "~/navigation";

import { type TourFromPage } from "../page";
import { TourCard } from "./tour-card";

export function AddToBagButton({
  datoTour,
  disabled,
  loading,
  plan,
  choice,
  passengerCategories,
  productId,
  startTime,
  totalPassengers,
}: {
  disabled: boolean;
  loading: boolean;
  datoTour: TourFromPage;
  productId: ProductId;
} & Pick<
  TourCalendar,
  "plan" | "passengerCategories" | "choice" | "startTime" | "totalPassengers"
> & { choice: NonNullable<TourCalendar["choice"]> }) {
  const track = useTrack();
  const pathname = usePathname();
  const isSSR = useIsSSR();
  const enabled = !disabled && !isSSR;
  const { addTourCartLine } = useCart();
  const t = useTranslations("Checkout");
  const tTourDetails = useTranslations("TourDetails");

  return (
    <Button
      className="w-full"
      size="lg"
      type="submit"
      variant="sunset"
      disabled={!enabled || loading || addTourCartLine.isPending}
      onClick={(event) => {
        event.preventDefault();
        if (!enabled) return;
        void addTourCartLine
          .mutateAsync({
            date: choice.date,
            datoTourId: datoTour.id,
            passengerCategories,
            planId: plan.id,
            productId,
            startTimeId: startTime?.id,
          })
          .then(() => {
            track({
              name: "Add To Cart",
              type: productId.type,
              datoTourId: datoTour.id,
              pathname,
            });
            toast({
              description: (
                <TourCard
                  amount={choice.amount}
                  date={choice.date}
                  totalCount={totalPassengers}
                  tour={{
                    heroImage: datoTour.heroImage,
                    id: datoTour.id,
                    location: datoTour.location,
                    slug: datoTour.slug,
                    title: datoTour.title,
                    tripadvisorRating: null,
                  }}
                />
              ),
              heading: (
                <div className="flex w-full items-center justify-center gap-2">
                  <CheckmarkFilledIcon className="size-4" />
                  <div className="mb-0.5 leading-none">
                    {tTourDetails("addedToBag")}
                  </div>
                </div>
              ),
            });
          });
      }}
    >
      <div className="inline-flex items-center gap-2">
        {loading ? <LoadingSpinner /> : null}
        {t("addToBag")}
      </div>
    </Button>
  );
}
